import {
  ImageWrapper,
  Loader,
  deserify,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Typography,
} from '@mui/material';
import { CreationStatusEnum } from '@protos/content_management/content.db_pb';
import {
  ResourceInfo,
  TopicMiniInfoV2,
} from '@protos/content_management/content.common.apis_pb';
import { LessonInfo } from '@protos/learning_management/lms.lesson.common.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useState } from 'react';
import { useAppSelector, useDeserifiedSelector } from '../../../../reduxStore/reduxHooks';
import { isAssignableResource } from '../../utils';
import SelectableResourceCard from './SelectableResourceCard';
import { LockedIds, useContentLocks } from './useContentLocks';

interface IProps {
  subjectId: number;
  chapterId: number;
  topic: TopicMiniInfoV2;
  selected: Set<string>;
  limitReached: boolean;
  onAdd: (ids: string[]) => void;
  onRemove: (ids: string[]) => void;
  onPreview: (resourceId: string, topicId: number) => void;
}

export default function TopicSection(props: IProps) {
  const {
    subjectId,
    chapterId,
    topic,
    selected,
    limitReached,
    onAdd,
    onRemove,
    onPreview,
  } = props;
  const teacher_id = getLocalStorage('userId');
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const {
    ContentCommonAPIServiceV2ClientWithStatusCodeHandler,
    LessonCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const fetchLocks = useContentLocks();

  const [loading, setLoading] = useState<
    'idle' | 'loading' | 'completed' | 'error'
  >('idle');
  const [resources, setResources] = useState<ResourceInfo[]>([]);
  const [lessons, setLessons] = useState<LessonInfo[]>([]);
  const [locked, setLocked] = useState<LockedIds>({
    resourceIds: new Set(),
    lessonIds: new Set(),
  });

  // Loaded on first expand so a chapter doesn't fire two calls per topic up front.
  const load = async () => {
    if (loading !== 'idle' && loading !== 'error') return;
    try {
      setLoading('loading');
      const [resourceRes, lessonRes] = await Promise.all([
        ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchTopicResources(
          {
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            subjectId,
            chapterId,
            topicId: topic.topicId,
            sectionId: class_subject_info?.sectionId,
          }
        ),
        LessonCommonAPIServiceV1ClientWithStatusCodeHandler.fetchLessonsByModule(
          {
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            moduleId: topic.topicId,
            sectionId: class_subject_info?.sectionId,
          }
        ),
      ]);
      const seen = new Set<string>();
      const topicResources = (resourceRes?.data?.categoryResourceMap ?? [])
        .flatMap((category) => category.categoryResources)
        .filter((resource) => {
          // The same resource can appear under more than one category.
          if (
            !isAssignableResource(resource, class_subject_info?.sectionId) ||
            seen.has(resource.resourceId)
          )
            return false;
          seen.add(resource.resourceId);
          return true;
        });
      const topicLessons = (lessonRes?.data?.lessons ?? []).filter(
        (lesson) =>
          lesson.resourceIds.length > 0 &&
          lesson.creationStatus !== CreationStatusEnum.CREATION_STATUS_DRAFT
      );
      setResources(topicResources);
      setLessons(topicLessons);
      setLocked(
        await fetchLocks(
          topicResources.map((r) => r.resourceId),
          topicLessons.map((l) => l.lessonId)
        )
      );
      setLoading('completed');
    } catch (err) {
      console.log(err);
      setLoading('error');
    }
  };

  const isLessonChecked = (lesson: LessonInfo) =>
    lesson.resourceIds.every((id) => selected.has(id));

  const toggleLesson = (lesson: LessonInfo) => {
    if (!isLessonChecked(lesson)) {
      onAdd(lesson.resourceIds);
      return;
    }
    // Keep resources that another checked lesson in this topic still needs.
    const keep = new Set(
      lessons
        .filter((l) => l.lessonId !== lesson.lessonId && isLessonChecked(l))
        .flatMap((l) => l.resourceIds)
    );
    onRemove(lesson.resourceIds.filter((id) => !keep.has(id)));
  };

  return (
    <Accordion
      disableGutters
      onChange={(_, expanded) => expanded && load()}
      sx={{ boxShadow: 'none', border: '1px solid #CCE6FE' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h3">
          {topic.topicNo}. {topic.topicTitle}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: pxToRem(16), md: pxTovW(20) },
        }}
      >
        {loading === 'loading' ? (
          <Loader />
        ) : loading === 'error' ? (
          <Typography variant="bodyText" color="error.main">
            Could not load this topic. Collapse and expand to retry.
          </Typography>
        ) : (
          <>
            <Box>
              <Typography variant="h4" sx={{ mb: pxToRem(8) }}>
                Topic Resources
              </Typography>
              {resources.length === 0 ? (
                <Typography variant="bodyText">No resources</Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: pxToRem(10), md: pxTovW(16) },
                  }}
                >
                  {resources.map((resource) => {
                    const checked = selected.has(resource.resourceId);
                    return (
                      <SelectableResourceCard
                        key={resource.resourceId}
                        resource={resource}
                        checked={checked}
                        disabled={!checked && limitReached}
                        locked={locked.resourceIds.has(resource.resourceId)}
                        onToggle={() =>
                          checked
                            ? onRemove([resource.resourceId])
                            : onAdd([resource.resourceId])
                        }
                        onPreview={() =>
                          onPreview(resource.resourceId, topic.topicId)
                        }
                      />
                    );
                  })}
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="h4" sx={{ mb: pxToRem(8) }}>
                Lesson Plans
              </Typography>
              {lessons.length === 0 ? (
                <Typography variant="bodyText">No lesson plans</Typography>
              ) : (
                lessons.map((lesson) => {
                  const checked = isLessonChecked(lesson);
                  const isLocked = locked.lessonIds.has(lesson.lessonId);
                  return (
                    <Box
                      key={lesson.lessonId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: pxToRem(10), md: pxTovW(12) },
                        py: pxToRem(4),
                        opacity: isLocked ? 0.5 : 1,
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={isLocked || (!checked && limitReached)}
                        onChange={() => toggleLesson(lesson)}
                        inputProps={{
                          'aria-label': `${isLocked ? 'Locked: ' : 'Select '}${
                            lesson.title
                          }`,
                        }}
                      />
                      <ImageWrapper
                        name="lesson-plan"
                        type="png"
                        parentFolder="icons"
                        path={getMediaBasePath(
                          lesson.posterImageUrl,
                          'processedMediaBucket'
                        )}
                        styles={{
                          width: { xs: pxToRem(60), md: pxTovW(80) },
                          height: { xs: pxToRem(40), md: pxTovW(54) },
                          borderRadius: pxToRem(6),
                          objectFit: 'cover',
                        }}
                      />
                      <Box>
                        <Typography variant="h4">{lesson.title}</Typography>
                        <Typography variant="smallText" color="primary.main">
                          {lesson.resourceIds.length} resources ·{' '}
                          {user_info?.teacherProfileId?.toString() ===
                          lesson.createdBy.toString()
                            ? 'You'
                            : lesson.teacherName || 'Geneo'}
                          {isLocked ? ' · Locked' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
